import './SimSchedule.scss';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AppSelect from '../../components/AppSelect/AppSelect';
import Button from '@mui/material/Button';
import { NoFleetSim } from './NoFleetSim';
import { useSimScheduleProvider } from '../../providers/SimScheduleProvider';
import Loader from '../../components/Loader/Loader';
import { DevicesResponse } from '../../models/SimScheduleInterface';
import { useProfile } from '../../providers/Profile';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { sanitizeHtml } from '../../helpers/sanitizeHtml';

export default function SimSchedule() {
  const { t } = useTranslation();
  // Initialize from localStorage to keep selection on refresh
  const [equipment, setEquipment] = useState<string | undefined>(() => {
    try {
      return localStorage.getItem('simSchedule.selectedEquipment') || undefined;
    } catch {
      return undefined;
    }
  });
  const { equipmentOptions, getEquipmentsData, getDevicesData, getDeviceReportUrl } = useSimScheduleProvider();
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceClickLoading, setDeviceClickLoading] = useState<string | null>(null);
  const { profile } = useProfile();
  const [equipmentLoading, setEquipmentLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [errMsg, setErrMsg] = useState<string>('');

  const [devicesData, setDevicesData] = useState<DevicesResponse>({ deviceList: [] });

  const vsimData = devicesData.deviceList.filter(d => d.type === 'VSIM');
  const ptData = devicesData.deviceList.filter(d => d.type === 'PT');
  const simData = devicesData.deviceList.filter(d => d.type === 'SIM');

  const handleDeviceClick = async (deviceId: string) => {
    const reportWindow = window.open('', '_blank');

    try {
      setDeviceClickLoading(deviceId); // Set loading for this specific device

      const res = await getDeviceReportUrl(deviceId);
      if (res.reportUrl) {
        if (reportWindow) {
          reportWindow.opener = null;
          reportWindow.location.href = res.reportUrl;
        } else {
          window.location.href = res.reportUrl;
        }
      } else {
        reportWindow?.close();
        setShowModal(true);
        setErrMsg(res.message || "No report is available for this device at the moment.");
      }
    } catch (error) {
      reportWindow?.close();
      console.error('Error opening device report:', error);
    } finally {
      setDeviceClickLoading(null); // Reset loading state
    }
  };

  useEffect(() => {
    setEquipmentLoading(true);
    getEquipmentsData();
  }, [getEquipmentsData]);

  useEffect(() => {
    if (equipmentOptions.length > 0) {
      setEquipmentLoading(false);
    }
  }, [equipmentOptions]);

  const handleEquipmentChange = useCallback(async (selected: string) => {
    setEquipment(selected);
    // Persist selection
    try {
      if (selected) {
        localStorage.setItem('simSchedule.selectedEquipment', selected);
      } else {
        localStorage.removeItem('simSchedule.selectedEquipment');
      }
    } catch { }
    if (selected) {
      setDevicesLoading(true);
      const data = await getDevicesData(selected);
      setDevicesData(data);
      setDevicesLoading(false);
    } else {
      setDevicesData({ deviceList: [] });
    }
  }, [getDevicesData]);

  // Initialize selection once and fetch devices when options are available
  const bootstrapped = useRef(false);
  const isUnloadingRef = useRef(false);

  // Mark when the page is unloading (refresh/close)
  useEffect(() => {
    const handleBeforeUnload = () => {
      isUnloadingRef.current = true;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // On unmount (SPA navigation), clear the saved selection
  // useEffect(() => {
  //   return () => {
  //     if (!isUnloadingRef.current) {
  //       try {
  //         localStorage.removeItem('simSchedule.selectedEquipment');
  //       } catch { }
  //     }
  //   };
  // }, []);

  useEffect(() => {
    if (bootstrapped.current) return;
    if (!equipmentOptions || equipmentOptions.length === 0) return;

    let saved: string | null = null;
    try {
      saved = localStorage.getItem('simSchedule.selectedEquipment');
    } catch { }

    const preferred = saved || profile?.results?.equipment;
    if (preferred) {
      bootstrapped.current = true;
      // Always trigger fetch for the preferred selection
      handleEquipmentChange(preferred);
    } else {
      // Nothing to load, but mark as bootstrapped to avoid re-running
      bootstrapped.current = true;
    }
  }, [equipmentOptions, profile, handleEquipmentChange]);

  const Modal = useMemo(() => ({ open, handleDialogClose, data }) => {
    return (
      <Dialog
        open={open}
        onClose={handleDialogClose}
        className="ac-dialog"
        maxWidth="lg"
        disableEscapeKeyDown={true}
      >
        <DialogTitle>
          <div>
            <h2 className="title">{data.title}</h2>
          </div>
        </DialogTitle>
        <DialogContent className="dialog-content">
          <p
            className="description"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.description) }}
            id="acDialog__description"
          />
          {data.support && (
            <p
              className="description-support"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.support) }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <div className="buttons">
            {(data.handleAction || data.actionLabel) && (
              <Button
                className={'btn-white'}
                onClick={() => data.handleAction()}
              >
                <span>{data.actionLabel}</span>
              </Button>
            )}
            {data.handleSecondaryAction && (
              <Button
                className="btn-black"
                onClick={() => data.handleSecondaryAction()}
              >
                <span>{data.secondaryActionLabel}</span>
              </Button>
            )}
          </div>
        </DialogActions>
      </Dialog>
    );
  }, []);

  return (
    <>
      {equipmentLoading ? <Loader /> : (
        <>
          <div className='ss-header__container'>
            <div className='ss-header__title'>
              <h1>{t('simSchedule.title')}</h1>
            </div>
            <div className="ss-header__dropdown">
              <AppSelect
                fillElements={equipmentOptions}
                multiSelect={false}
                selectedItem={equipment}
                selectLabel="Equipment"
                onChange={handleEquipmentChange}
              />
            </div>

          </div>

          <div className="ss-body__container">
            {showModal && (
              <Modal
                open={showModal}
                handleDialogClose={() => setShowModal(false)}
                data={{
                  description: errMsg,
                  handleAction: () => setShowModal(false),
                  actionLabel: 'Close',
                }}
              />
            )}
            {devicesLoading ? (
              <Loader />
            ) : (<>
              {devicesData.deviceList.length > 0 ? (

                <div className="ss-fleet-type-row__box">

                  {/* Only show VSIM column if there's VSIM data */}
                  {vsimData.length > 0 && (
                    <div className="ss-fleet-type-column__box">
                      <p className='ss-fleet-type__tag'>Virtual Simulator <br /> (VSIM)</p>
                      {vsimData.map((item, idx) => (
                        <Button
                          className='ss-fleet__button'
                          key={item.id + idx}
                          onClick={() => handleDeviceClick(item.id)}
                          disabled={deviceClickLoading === item.id}
                        >
                          {deviceClickLoading === item.id ? (
                            <>
                              <span>Loading...</span>
                              {/* You can add a spinner here if you have one */}
                            </>
                          ) : (
                            item.name
                          )}
                        </Button>
                      ))}
                    </div>
                  )}


                  {/* Only show PT column if there's PT data */}
                  {ptData.length > 0 && (
                    <div className="ss-fleet-type-column__box">
                      <p className='ss-fleet-type__tag'>Procedural Trainer <br /> (PT)</p>
                      {ptData.map((item, idx) => (
                        <Button
                          className='ss-fleet__button'
                          key={item.id + idx}
                          onClick={() => handleDeviceClick(item.id)}
                          disabled={deviceClickLoading === item.id}
                        >
                          {deviceClickLoading === item.id ? (
                            <>
                              <span>Loading...</span>
                            </>
                          ) : (
                            item.name
                          )}
                        </Button>
                      ))}
                    </div>
                  )}


                  {/* Only show SIM column if there's SIM data */}
                  {simData.length > 0 && (
                    <div className="ss-fleet-type-column__box">
                      <p className='ss-fleet-type__tag'>Full Flight Simulator <br /> (SIM)</p>
                      {simData.map((item, idx) => (
                        <Button
                          className='ss-fleet__button'
                          key={item.id + idx}
                          onClick={() => handleDeviceClick(item.id)}
                          disabled={deviceClickLoading === item.id}
                        >
                          {deviceClickLoading === item.id ? (
                            <>
                              <span>Loading...</span>
                            </>
                          ) : (
                            item.name
                          )}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>) : (
                <NoFleetSim />
              )}
            </>)}
          </div>

        </>)}

    </>
  );
}